import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { merge, Subject, takeUntil } from 'rxjs';
import axios, { AxiosError } from 'axios';
import { environment } from 'src/environments/environment';
import { PayeeCasesService } from '../payee-cases.service';
import { ChipDropdownComponent } from '../chip-dropdown/chip-dropdown.component';
import { PayeeFormFieldComponent } from '../../payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeAutocompleteComponent } from '../../payee-form-fields/payee-autocomplete/payee-autocomplete.component';
import { PayeeButtonComponent } from '../../payee-buttons/payee-button/payee-button.component';

type StateLabelOption = {
  label: string;
};

@Component({
  selector: 'payee-case-state-filter',
  templateUrl: './case-state-filter.component.html',
  styleUrls: ['./case-state-filter.component.scss'],
  imports: [
    ChipDropdownComponent,
    PayeeFormFieldComponent,
    PayeeAutocompleteComponent,
    FormsModule,
    ReactiveFormsModule,
    PayeeButtonComponent,
    TranslateModule,
  ],
})
export class CaseStateFilterComponent implements OnInit, OnDestroy {
  private payeeCasesService = inject(PayeeCasesService);
  private translate = inject(TranslateService);
  private snackbar = inject(MatSnackBar);

  private readonly destroy = new Subject<void>();

  dropdownOpen = false;

  readonly stateControl = new FormControl<string | null>(null);
  stateOptions: string[] = [];

  get state() {
    return this.payeeCasesService.state.value;
  }

  ngOnInit(): void {
    this.payeeCasesService.state
      .pipe(takeUntil(this.destroy))
      .subscribe((v) => this.stateControl.patchValue(v, { emitEvent: false }));

    merge(this.translate.onLangChange, this.payeeCasesService.cases)
      .pipe(takeUntil(this.destroy))
      .subscribe(async () => {
        await this.setStates();
      });
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  applyFilter(): void {
    this.payeeCasesService.state.next(this.stateControl.value);
    this.dropdownOpen = false;
  }

  clearFilter(): void {
    this.payeeCasesService.state.next(null);
    this.dropdownOpen = false;
  }

  private async setStates(): Promise<void> {
    const url = `${environment.baseUrl}/api/case-states`;

    try {
      const result = await axios.get<{ states: StateLabelOption[] }>(url, {
        params: {
          ...this.payeeCasesService.getFilters(),
          state: null,
        },
      });

      this.stateOptions = result.data.states.map((option) => option.label);
    } catch (error: AxiosError | unknown) {
      if (error instanceof AxiosError) {
        console.error(
          'Network error while getting states',
          error.response?.data,
        );
      } else {
        console.error('Unknown error while getting states', error);
      }

      this.snackbar.open(
        this.translate.instant(
          'CASES_TABLE.ERROR_MESSAGES.ERROR_WHILE_LOADING_STATES',
        ),
        'OK',
        { duration: 5000 },
      );
    }
  }
}
